import React, { useState, useEffect } from 'react';
import { LEARNOSITY_QUESTION_TYPE_NAMES as questionNames } from '@utils/constants';
import { useOptionalGetStandardByGuid } from '@components/ContentFilters/ContentFilterProvider';
import Box from '../Atoms/Box';
import Typography from '../Atoms/Typography';
import Chip from '../Atoms/Chip';
import Dialog, { DialogContent } from '../Atoms/Dialog/Dialog';

interface AlgoliaLearnosityItem {
  objectID: string;
  [key: string]: any;
}

interface TagEntry {
  key: string;
  values: string[];
}

interface AlgoliaTagsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  hit: AlgoliaLearnosityItem | null;
}

// Define tag field candidates - these are the fields we expect to contain tags
// and their corresponding display names
const tagFields = {
  objectID: 'Question ID',
  'Blooms Taxonomy': 'Blooms Taxonomy',
  'Depth Of Knowledge': 'Depth Of Knowledge',
  Difficulty: 'Difficulty',
  'Grade Levels': 'Grade Levels',
  'Question Type': 'Question Type',
  'Skill Display': 'Skill',
  'AB GUID': 'Standard',
};

/**
 * Dialog for displaying all tags associated with an Algolia hit
 */
const AlgoliaTagsDialog: React.FC<AlgoliaTagsDialogProps> = ({
  isOpen,
  onClose,
  hit,
}) => {
  const getStandardByGuid = useOptionalGetStandardByGuid();
  const [tagEntries, setTagEntries] = useState<TagEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hit) {
      return;
    }

    try {
      const entries = Object.keys(tagFields).map((key) => {
        let value = hit[key] ?? '-';

        if (key === 'Question Type') {
          value = value in questionNames ? [questionNames[value]] : [value];
        }

        if (key === 'Skill Display') {
          value = hit['Skill Display'] ||
            hit.Skill?.map((name: string) => name.replace(/-/g, ' ')) || ['-'];
        }

        if (key === 'AB GUID') {
          value = hit[key]
            ?.map(getStandardByGuid)
            ?.filter(Boolean)
            ?.map((standard) => standard.code) || ['-'];
        }

        if (Array.isArray(value)) {
          value = value.filter(Boolean);
        } else {
          value = [value];
        }

        return { key: tagFields[key], values: value };
      });
      setTagEntries(entries);
    } catch (err) {
      setError('Error processing tag data');
    }
  }, [hit, getStandardByGuid]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={error ? 'Error' : `All Tags (${tagEntries.length})`}
      showCloseIcon
      aria-labelledby="tags-dialog-title"
      sx={{
        '& .MuiDialog-paper': {
          width: '640px',
          minWidth: '640px',
          minHeight: '620px',
          maxHeight: '620px',
        },
      }}
    >
      <DialogContent>
        {error ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: 'error.main',
            }}
          >
            <Typography>{error}</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '530px',
              padding: '5px',
              overflow: 'auto',
            }}
            role="list"
            aria-label="Item tags"
          >
            {tagEntries.map(({ key, values }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <Typography sx={{ width: '150px', flexShrink: 0, lineHeight: '1.8rem' }}>
                  {key}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {values.map((value) => (
                    <Chip
                      key={`${key}-${value}`}
                      label={value}
                      variant="outlined"
                      sx={{ borderRadius: '16px' }}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AlgoliaTagsDialog;
